import { useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { loadCalendarMonth, loadClosureDays, type AbsenceFactRow } from './data';
import { fromDateKey } from '@/lib/supabase';
import { useIsNarrow } from '@/lib/useMediaQuery';
import { TechnicalDetails } from '@/lib/TechnicalDetails';
// Direction D redesign (2026-09-04): colors/radii/spacing only, per the
// task's explicit constraint — no aria-*/role/keyboard/shape logic below is
// touched. See theme.ts for what each token means.
import { color, font, radius } from '@/lib/theme';

/**
 * Team Time-Off Calendar — read-only, location-scoped for free by RLS.
 *
 * What shows and what doesn't (this is the reader BUILD_PLAN D3 warned about):
 *   - Approved / Recorded / Needs Documentation → solid pill
 *   - Pending → dashed, tentative — shown, as the live system shows them
 *   - Denied and Voided → absent entirely (DD2: a voided record drops out of
 *     all calendar math; the live system's non-Denied bucketing would have
 *     kept showing a voided person as off)
 *
 * Weekends and closure days render greyed out (Scott's tinker feedback,
 * 2026-08-24 — "easy viewing at a glance"). Weekends are day-of-week
 * arithmetic, which §7.2(a) sanctions client-side; CLOSURES come from the
 * database via closure_days() (migration 0013), one call per month — the
 * shading this file's own P2 comment deferred until the SQL helper existed.
 * The rule engine stays in exactly one place.
 *
 * Pre-P8 punch list, Tier 2 (mobile) + Tier 3 (a11y) — three findings
 * about this one component, fixed together because they share a structure:
 *
 *   1. The 7-column grid broke on phone widths: `repeat(7, 1fr)` lets a
 *      column grow to its content's min-width, and a nowrap pill is wide,
 *      so the grid overflowed the page sideways. `minmax(0, 1fr)` pins the
 *      columns to an equal seventh no matter what is inside them.
 *   2. Pills truncated and relied on a hover-only `title` — nothing happened
 *      on tap. Now any day WITH entries is a button (the whole cell, ≥44px
 *      tall) that opens a details panel under the calendar listing every
 *      entry in full: full name, absence type, status. On a phone the pills
 *      themselves give way to compact markers (a round dot for PTO, a
 *      square for any other absence, hollow for pending — shape as well as
 *      colour, WCAG 1.4.1) with a legend; a 38-53px-wide cell cannot hold
 *      readable text, and the panel is one tap away. Tap the same day
 *      again, Close, or Escape to dismiss.
 *   3. `role="grid"` had no row/cell markup underneath it, so a screen reader
 *      saw 40-odd loose divs. The markup is now a proper ARIA table: one
 *      row of columnheaders, then one row per week, each with exactly seven
 *      cells (the last week padded with blank cells). `role="table"` rather
 *      than `grid` on purpose: grid implies arrow-key navigation between
 *      focusable cells, which this read-mostly calendar does not implement —
 *      the day buttons are ordinary Tab stops instead, and screen-reader
 *      table navigation already walks every day in order, empty or not.
 *
 * Also here (Tier 3 polish): a failed month fetch used to show the raw error
 * string with no way to retry — now a plain sentence, a "Try again" button
 * (which stays put while it retries, so focus is never dropped) and the
 * technical text behind a disclosure.
 *
 * Focus is handed back deliberately wherever a control removes itself: Close
 * and Escape return to the day button that opened the panel, Today and a
 * successful retry land on the month label. Three lenses of adversarial
 * review found each of these before this shipped.
 */

const DOW = [
  { short: 'Sun', long: 'Sunday' },
  { short: 'Mon', long: 'Monday' },
  { short: 'Tue', long: 'Tuesday' },
  { short: 'Wed', long: 'Wednesday' },
  { short: 'Thu', long: 'Thursday' },
  { short: 'Fri', long: 'Friday' },
  { short: 'Sat', long: 'Saturday' },
] as const;

const DAY_PANEL_ID = 'team-calendar-day';
const DAY_PANEL_TITLE_ID = 'team-calendar-day-title';

export function TeamCalendar({
  today,
  names,
  visibleIds,
  reloadKey = 0,
}: {
  today: string;
  names: Map<string, string>;
  /**
   * P6: the admin location filter. null = show every employee's entries.
   * When set, a day's entries are restricted to ids in the set — it does NOT
   * relabel anyone. `names` stays the full active-roster map regardless, so
   * the ONE case this can't attribute to a location — a genuinely
   * deactivated employee's history, which has no entry in `names` at all —
   * still falls back to "(former)" rather than a wrong label, in the
   * unfiltered ("All locations") view. Adversarial-review correction: under
   * an ACTIVE location filter a former employee's entries are excluded too
   * (their location is unknowable, same "unattributable, so excluded" call
   * ManageRecords' own filter documents for the identical case) — this is
   * NOT the "(former)" fallback rendering, it is filtered out before that
   * fallback is ever reached. An earlier version of this comment claimed
   * otherwise; it was wrong.
   */
  visibleIds: Set<string> | null;
  /**
   * Bumped by the Dashboard after any write (approve/deny/log/void…). The
   * month is refetched — quietly, without blanking the grid — when it
   * changes, so the calendar and the details panel's spelled-out status
   * never say "Pending" about a request the manager just approved in the
   * section beneath it (review finding; before, only a month change or a
   * full reload ever refetched).
   */
  reloadKey?: number;
}) {
  // Month arithmetic on numbers, not Date strings — trap #1 lives in parsing.
  const [ty, tm] = [Number(today.slice(0, 4)), Number(today.slice(5, 7))];
  const [year, setYear] = useState(ty);
  const [month, setMonth] = useState(tm); // 1–12
  const [facts, setFacts] = useState<AbsenceFactRow[] | null>(null);
  const [closures, setClosures] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  // Bumped by "Try again" after a failed fetch — re-runs the effect below.
  const [attempt, setAttempt] = useState(0);
  // True from tapping "Try again" until that attempt settles: the error box
  // stays mounted with the button relabelled, so focus is never dropped.
  const [retrying, setRetrying] = useState(false);
  // The one live status line, always mounted so it is reliably announced.
  const [statusText, setStatusText] = useState('Loading…');
  // The day whose entries are open in the panel under the calendar.
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  // The day button that opened the panel — Close/Escape hand focus back to
  // it (review finding: Close unmounting itself dropped focus to <body>).
  const openerRef = useRef<HTMLButtonElement | null>(null);
  const monthLabelRef = useRef<HTMLSpanElement | null>(null);
  // Which month the grid currently shows, to tell a month change (blank the
  // grid while the new one loads) from a quiet refetch (keep it).
  const shownMonthRef = useRef('');
  const narrow = useIsNarrow();
  const displayNames = useMemo(() => shortNames(names), [names]);

  useEffect(() => {
    let stale = false;
    const monthKey = `${year}-${month}`;
    const monthChanged = shownMonthRef.current !== monthKey;
    shownMonthRef.current = monthKey;
    if (monthChanged) setFacts(null);
    // A retry keeps the error box mounted ("Retrying…"); a month change or a
    // quiet refetch clears any old error.
    if (!retrying) setError(null);
    setStatusText('Loading…');
    const wasRetry = retrying;
    Promise.all([loadCalendarMonth(year, month), loadClosureDays(year, month)]).then(
      ([rows, closed]) => {
        if (stale) return;
        setFacts(rows);
        setClosures(closed);
        setError(null);
        setRetrying(false);
        setStatusText(wasRetry ? 'Calendar loaded.' : '');
        if (wasRetry) monthLabelRef.current?.focus();
      },
      (e: unknown) => {
        if (stale) return;
        setError(e instanceof Error ? e.message : String(e));
        setRetrying(false);
        // The alert text is unchanged on a failed retry, so it is not re-read;
        // the persistent status line says what happened instead.
        setStatusText(wasRetry ? 'Still could not load the calendar.' : '');
      },
    );
    return () => {
      stale = true;
    };
    // `retrying` is read for the wording only; it must not restart the fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month, attempt, reloadKey]);

  // The panel can open below the fold (a first-week day in a six-row month,
  // or a calendar scrolled mid-screen) with only the cell highlight as
  // feedback — bring it into view. 'nearest' is a no-op when it already is.
  useEffect(() => {
    if (selectedKey !== null) {
      document.getElementById(DAY_PANEL_ID)?.scrollIntoView({ block: 'nearest', behavior: 'auto' });
    }
  }, [selectedKey]);

  function goTo(y: number, m: number) {
    setYear(y);
    setMonth(m);
    // A selected day belongs to the month it was picked in.
    setSelectedKey(null);
  }

  function shift(delta: number) {
    const next = year * 12 + (month - 1) + delta;
    goTo(Math.floor(next / 12), (next % 12) + 1);
  }

  const byDate = new Map<string, AbsenceFactRow[]>();
  for (const f of facts ?? []) {
    const list = byDate.get(f.absence_date);
    if (list) list.push(f);
    else byDate.set(f.absence_date, [f]);
  }
  const entriesFor = (key: string): AbsenceFactRow[] =>
    (byDate.get(key) ?? []).filter((f) => visibleIds === null || visibleIds.has(f.employee_id));

  const mm = String(month).padStart(2, '0');
  const daysInMonth = new Date(year, month, 0).getDate();
  const firstDow = new Date(year, month - 1, 1).getDay(); // 0 = Sunday
  const monthLabel = new Date(year, month - 1, 1).toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
  });

  // Leading blanks so day 1 lands under its weekday, then the days themselves,
  // chunked into weeks of exactly seven (the last week padded with blanks) so
  // every ARIA row has the same cell count and every CSS row the same columns.
  const cells: Array<number | null> = [
    ...Array.from({ length: firstDow }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];
  const weeks: Array<Array<number | null>> = [];
  for (let i = 0; i < cells.length; i += 7) {
    const week = cells.slice(i, i + 7);
    while (week.length < 7) week.push(null);
    weeks.push(week);
  }

  const selectedEntries = selectedKey === null ? [] : entriesFor(selectedKey);

  // A selected day whose entries have all been filtered out (the admin
  // changed the location filter) would keep a highlighted, now-inert cell
  // and a panel with nothing to show — close it instead.
  useEffect(() => {
    if (selectedKey !== null && facts !== null && selectedEntries.length === 0) setSelectedKey(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- selectedEntries is derived from these
  }, [selectedKey, facts, visibleIds]);

  function closePanel() {
    setSelectedKey(null);
    // Back to the button that opened it; if the filter demoted that day to a
    // static cell meanwhile, the month label is the next-best anchor.
    const opener = openerRef.current;
    if (opener && opener.isConnected) opener.focus();
    else monthLabelRef.current?.focus();
  }

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Escape' && selectedKey !== null) {
      e.preventDefault();
      closePanel();
    }
  }

  return (
    <div onKeyDown={onKeyDown}>
      <div style={S.bar}>
        <button onClick={() => shift(-1)} style={S.navBtn} aria-label="Previous month">
          ‹
        </button>
        {/* aria-live so a screen-reader user hears which month the arrows
            landed on; focusable (tabIndex -1) so Today / a successful retry /
            a lost Close target have somewhere sensible to land. */}
        <span ref={monthLabelRef} tabIndex={-1} style={S.monthLabel} aria-live="polite">
          {monthLabel}
        </span>
        <button onClick={() => shift(1)} style={S.navBtn} aria-label="Next month">
          ›
        </button>
        {(year !== ty || month !== tm) && (
          <button
            onClick={() => {
              goTo(ty, tm);
              // This button removes itself once we are on the current month.
              monthLabelRef.current?.focus();
            }}
            style={S.todayBtn}
          >
            Today
          </button>
        )}
        {/* One persistent status line (an inserted role=status is not
            reliably announced; text changes on an existing one are). */}
        <span role="status" style={S.loading}>
          {statusText}
        </span>
      </div>

      {error && (
        // role="alert" on the sentence only; the button and the disclosure are
        // siblings, so neither is announced as part of the alert and opening
        // the disclosure does not re-announce it (the branch's pattern).
        <div style={S.errorBox}>
          <p role="alert" style={S.errorText}>
            Could not load this month’s calendar. Check your connection and try again.
          </p>
          <button
            type="button"
            onClick={() => {
              if (retrying) return;
              setRetrying(true);
              setAttempt((a) => a + 1);
            }}
            // aria-disabled, not disabled: Chrome blurs a button the moment it
            // becomes disabled, which is exactly the focus drop this guards
            // against. The click handler ignores repeats instead.
            aria-disabled={retrying || undefined}
            aria-busy={retrying || undefined}
            style={{ ...S.retryBtn, ...(retrying ? S.retryBtnBusy : null) }}
          >
            {retrying ? 'Retrying…' : 'Try again'}
          </button>
          <TechnicalDetails detail={error} />
        </div>
      )}

      {/* Under an error the month is unknown — an empty grid would read as
          "nobody is off", the exact false signal trap #3 warns about. */}
      {!error && (
        <div role="table" aria-label={`Team time off, ${monthLabel}`}>
          <div role="row" style={S.weekRow}>
            {DOW.map((d) => (
              // A visually-hidden full name rather than <abbr title>, which
              // mobile screen readers largely ignore. The uppercase styling
              // sits on the visible span only, so the hidden name keeps its
              // natural case for the accessible name.
              <div key={d.short} role="columnheader" style={S.dow}>
                <span aria-hidden="true" style={S.dowShort}>
                  {d.short}
                </span>
                <span style={S.srOnly}>{d.long}</span>
              </div>
            ))}
          </div>
          {weeks.map((week, wi) => (
            <div key={wi} role="row" style={S.weekRow}>
              {week.map((day, di) => {
                if (day === null) {
                  return (
                    <div
                      key={`blank-${wi}-${di}`}
                      role="cell"
                      style={narrow ? S.blankNarrow : S.blank}
                    />
                  );
                }
                const key = `${year}-${mm}-${String(day).padStart(2, '0')}`;
                const entries = entriesFor(key);
                const isToday = key === today;
                // Grey the days the shop is not open: weekends by day-of-week
                // (client-side, sanctioned), closures from the database (0013).
                const dow = (firstDow + day - 1) % 7;
                const isWeekend = dow === 0 || dow === 6;
                const isClosure = closures.has(key);
                const selected = key === selectedKey && entries.length > 0;
                const hasButton = entries.length > 0;
                // Padding lives on the BUTTON when there is one, so the whole
                // column width is tappable (a phone column is ~38px; every
                // pixel of chrome outside the button is dead target).
                const cellStyle: React.CSSProperties = {
                  ...S.cell,
                  ...(narrow ? S.cellNarrow : null),
                  ...(hasButton ? S.cellWithButton : narrow ? S.cellPadNarrow : S.cellPad),
                  // Visual-fidelity fix (adversarial review, 2026-09-04): the
                  // hatch is the mockup's CLOSURE-day treatment only — an
                  // ordinary weekend stays flat grey (cellWeekend), same as
                  // before this pass introduced the hatch texture.
                  ...(isClosure ? S.cellOff : isWeekend ? S.cellWeekend : null),
                  ...(isToday ? S.cellToday : null),
                  ...(selected ? S.cellSelected : null),
                };
                // The "closed" text tag needs ~55px of unbreakable width — on a
                // 375px phone a cell's content box is ~30px, so it would paint
                // over the neighbouring day. There, the grey shading (spelled
                // out in the legend) and the button's aria-label carry it.
                const dayNum = (
                  <div style={S.dayNum}>
                    {day}
                    {isClosure && !narrow && <span style={S.closedTag}>closed</span>}
                  </div>
                );

                if (!hasButton) {
                  return (
                    <div key={key} role="cell" style={cellStyle} title={isClosure ? 'Shop closed' : undefined}>
                      {dayNum}
                    </div>
                  );
                }

                // Everything a screen reader needs is in the label — the pills
                // (or markers) inside are decoration once the label is set.
                const label =
                  `${longDate(key)}${isClosure ? ', shop closed' : ''}: ` +
                  `${entries.length} ${entries.length === 1 ? 'entry' : 'entries'} — ` +
                  entries
                    .map(
                      (f) =>
                        `${displayNames.get(f.employee_id) ?? 'former employee'} ${f.absence_type}` +
                        `${f.status === 'Pending' ? ' (pending)' : ''}`,
                    )
                    .join(', ');

                return (
                  <div key={key} role="cell" style={cellStyle} title={isClosure ? 'Shop closed' : undefined}>
                    <button
                      type="button"
                      onClick={(e) => {
                        openerRef.current = e.currentTarget;
                        setSelectedKey(selected ? null : key);
                      }}
                      aria-expanded={selected}
                      aria-controls={DAY_PANEL_ID}
                      aria-label={label}
                      style={{ ...S.cellBtn, ...(narrow ? S.cellPadNarrow : S.cellPad) }}
                    >
                      {dayNum}
                      {narrow ? (
                        // Decorative: the button's aria-label is the sole
                        // accessible description of what is on the day.
                        <span style={S.dots} aria-hidden="true">
                          {entries.map((f) => (
                            <span key={f.id} style={{ ...S.dot, ...dotStyle(f) }} />
                          ))}
                        </span>
                      ) : (
                        entries.map((f) => (
                          <span
                            key={f.id}
                            style={{
                              ...S.entry,
                              ...(f.absence_type === 'PTO' ? S.entryPto : S.entryOther),
                              ...(f.status === 'Pending' ? S.entryPending : null),
                            }}
                            title={`${f.absence_type} — ${f.status}`}
                          >
                            {displayNames.get(f.employee_id) ?? '(former)'} · {typeShort(f.absence_type)}
                            {f.status === 'Pending' ? ' ?' : ''}
                          </span>
                        ))
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {/* The markers are colour- AND shape-coded (round PTO, square other,
          hollow pending), so the phone view carries a legend the pills never
          needed (their text carried the type) — WCAG 1.4.1. */}
      {narrow && !error && (
        <p style={S.legend} aria-hidden="true">
          <span style={{ ...S.dot, ...S.dotPto }} /> PTO
          <span style={{ ...S.dot, ...S.dotOther, marginLeft: '0.9rem' }} /> other absence
          <span style={{ ...S.dot, ...S.dotPending, marginLeft: '0.9rem' }} /> pending
          <span style={{ ...S.swatchOff, marginLeft: '0.9rem' }} /> weekend / closed
          {' — tap a day with markers for details'}
        </p>
      )}

      {/* The selected day, in full. The live region (always mounted, so a
          content change is reliably announced) holds ONLY the title and the
          list; the Close button sits outside it so it is not read mid-
          sentence. The landmark role exists only while there is content. */}
      <div
        role={selectedKey !== null ? 'region' : undefined}
        aria-labelledby={selectedKey !== null ? DAY_PANEL_TITLE_ID : undefined}
        style={selectedKey !== null ? S.dayPanel : undefined}
      >
        <div id={DAY_PANEL_ID} aria-live="polite">
          {selectedKey !== null && (
            <>
              <p id={DAY_PANEL_TITLE_ID} style={S.dayPanelTitle}>
                {longDate(selectedKey)}
                {closures.has(selectedKey) ? ' · shop closed' : ''}
              </p>
              <ul style={S.dayPanelList}>
                {selectedEntries.map((f) => (
                  <li key={f.id} style={S.dayPanelItem}>
                    <span style={{ ...S.dot, ...dotStyle(f) }} aria-hidden="true" />
                    <span style={S.dayPanelName}>{names.get(f.employee_id) ?? '(former employee)'}</span>
                    <span style={S.dayPanelMeta}>
                      {f.absence_type} · {f.status}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
        {selectedKey !== null && (
          <button type="button" onClick={closePanel} style={S.closeBtn}>
            Close
          </button>
        )}
      </div>
    </div>
  );
}

/** "Tuesday, September 15" from a YYYY-MM-DD key — local parts only (trap #1). */
function longDate(key: string): string {
  return fromDateKey(key).toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}

/** The compact marker: PTO a green circle, anything else a red square, Pending hollow. */
function dotStyle(f: AbsenceFactRow): React.CSSProperties {
  if (f.status === 'Pending') return S.dotPending;
  return f.absence_type === 'PTO' ? S.dotPto : S.dotOther;
}

/**
 * First name alone — unless two employees share it (there are two Michaels on
 * staff today), in which case "First L." so a calendar pill is never ambiguous
 * about who is out.
 */
function shortNames(names: Map<string, string>): Map<string, string> {
  const firstNameCount = new Map<string, number>();
  for (const full of names.values()) {
    const first = full.split(' ')[0] ?? full;
    firstNameCount.set(first, (firstNameCount.get(first) ?? 0) + 1);
  }
  const out = new Map<string, string>();
  for (const [id, full] of names) {
    const parts = full.split(' ');
    const first = parts[0] ?? full;
    const last = parts.length > 1 ? parts[parts.length - 1] : undefined;
    const collides = (firstNameCount.get(first) ?? 0) > 1;
    out.set(id, collides && last !== undefined ? `${first} ${last.slice(0, 1)}.` : first);
  }
  return out;
}

function typeShort(t: AbsenceFactRow['absence_type']): string {
  switch (t) {
    case 'PTO':
      return 'PTO';
    case 'Called Off':
      return 'Off';
    case 'Sick':
      return 'Sick';
    case 'Tardy/Late':
      return 'Late';
    case 'No-Call-No-Show':
      return 'NCNS';
    case 'Other Unplanned':
      return 'Other';
    case 'Emergency':
      return 'Emerg';
  }
}

const S = {
  bar: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.6rem',
    marginBottom: '0.75rem',
    flexWrap: 'wrap',
  },
  // Pre-P8 punch list, Tier 3: was 1.8rem (~29px), under the ~44px touch-target
  // guideline. Bumped to meet it; font-size nudged up to match the bigger box.
  navBtn: {
    width: '2.75rem',
    height: '2.75rem',
    fontSize: '1.2rem',
    lineHeight: 1,
    border: '1px solid #ddd',
    borderRadius: radius.pill,
    background: 'white',
    cursor: 'pointer',
  },
  monthLabel: {
    fontWeight: 700,
    fontSize: '0.95rem',
    minWidth: '9.5rem',
    textAlign: 'center',
    fontFamily: font.display,
  },
  todayBtn: {
    minHeight: '2.75rem',
    padding: '0.2rem 0.8rem',
    fontSize: '0.85rem',
    border: '1px solid #ddd',
    borderRadius: radius.pill,
    background: 'white',
    cursor: 'pointer',
    color: '#444',
  },
  loading: { fontSize: '0.8rem', color: '#666' },
  errorBox: {
    margin: '0 0 0.75rem',
    padding: '0.7rem 0.85rem',
    borderRadius: '0.4rem',
    background: '#fdecef',
    color: '#b00020',
    fontSize: '0.85rem',
    lineHeight: 1.5,
  },
  errorText: { margin: 0 },
  // Brand blue, full width: the recovery action, same as the request form's
  // and the crash fallback's own "Try again" (review finding on colour).
  retryBtn: {
    display: 'block',
    width: '100%',
    marginTop: '0.5rem',
    minHeight: '2.75rem',
    padding: '0.5rem 1rem',
    fontSize: '0.88rem',
    fontWeight: 600,
    color: 'white',
    background: color.brandBlue,
    border: 0,
    borderRadius: radius.pill,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  retryBtnBusy: { opacity: 0.75, cursor: 'progress' },
  // minmax(0, 1fr): an equal seventh each, whatever the content's own width —
  // plain 1fr would let a nowrap pill push its column (and the page) wider.
  weekRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
    gap: '2px',
    marginBottom: '2px',
  },
  dow: {
    fontSize: '0.7rem',
    color: '#666',
    padding: '0.2rem 0.3rem',
  },
  dowShort: { textTransform: 'uppercase', letterSpacing: '0.05em' },
  // Visually hidden, still read by screen readers (the standard clip trick).
  srOnly: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: 0,
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
  legend: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '0.35rem',
    margin: '0.5rem 0 0',
    fontSize: '0.75rem',
    color: '#555',
  },
  swatchOff: {
    display: 'inline-block',
    width: '0.7rem',
    height: '0.7rem',
    borderRadius: radius.calendarCellMobile,
    background: '#eef0f3',
    border: '1px solid #d9dde3',
    flexShrink: 0,
  },
  blank: { minHeight: '4.2rem' },
  blankNarrow: { minHeight: '2.75rem' },
  cell: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '4.2rem',
    border: '1px solid #f0f0f0',
    borderRadius: radius.calendarCellDesktop,
    fontSize: '0.75rem',
    minWidth: 0,
  },
  // A phone cell: 44px tall minimum (the touch-target guideline).
  cellNarrow: { minHeight: '2.75rem', borderRadius: radius.calendarCellMobile },
  cellPad: { padding: '0.25rem 0.3rem' },
  cellPadNarrow: { padding: '0.2rem 0.2rem' },
  cellWithButton: { padding: 0 },
  cellToday: { border: `1px solid ${color.brandBlue}`, boxShadow: `inset 0 0 0 1px ${color.brandBlue}` },
  // Diagonal hatch, matching the spec's closure-day treatment — reserved for
  // actual shop-closure days (isClosure) only.
  cellOff: {
    background: `repeating-linear-gradient(135deg, ${color.closureStripeA}, ${color.closureStripeA} 6px, ${color.closureStripeB} 6px, ${color.closureStripeB} 12px)`,
    border: '1px solid #e4e7eb',
  },
  // Visual-fidelity fix (adversarial review, 2026-09-04): plain weekends
  // (isWeekend && !isClosure) get a flat tint, not the hatch — the mockup
  // only hatches true closure days, and every Saturday/Sunday is a weekend.
  cellWeekend: { background: color.closureStripeA, border: '1px solid #e4e7eb' },
  cellSelected: { background: '#e8f0fe', boxShadow: `inset 0 0 0 2px ${color.brandBlue}` },
  // The whole cell is the button when a day has entries: reset the button
  // chrome and let it fill the cell so the tap target is the full cell.
  cellBtn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    flex: '1 1 auto',
    width: '100%',
    minWidth: 0,
    margin: 0,
    border: 0,
    borderRadius: radius.calendarCellDesktop,
    background: 'none',
    font: 'inherit',
    color: 'inherit',
    textAlign: 'left',
    cursor: 'pointer',
  },
  closedTag: {
    marginLeft: '0.35rem',
    padding: '0 0.3rem',
    borderRadius: '999px',
    background: '#e0e3e8',
    color: '#555',
    fontSize: '0.6rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    verticalAlign: 'middle',
  },
  dayNum: { fontSize: '0.7rem', color: '#666', marginBottom: '0.15rem' },
  entry: {
    display: 'block',
    borderRadius: '0.3rem',
    padding: '0.08rem 0.3rem',
    marginBottom: '0.15rem',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontWeight: 600,
  },
  // PTO → brand blue, matching the spec's "PTO status" mapping (and the
  // calendar's own already-blue cellToday/cellSelected — no change needed
  // there since they already match the new brand color exactly).
  entryPto: { background: color.chipBrandBg, color: color.brandBlue },
  // Every non-PTO absence type still shares one bucket/color today (the
  // existing two-bucket model, unchanged) — recolored to teal, the closest
  // secondary accent in the approved palette. Per-type coloring (a distinct
  // amber for Tardy, say) would need to change dotStyle()'s branching logic,
  // which is out of this file's colors/radii/spacing-only scope this pass.
  entryOther: { background: color.chipTealBg, color: color.teal },
  entryPending: {
    background: 'white',
    border: `1px dashed ${color.pendingDash}`,
    color: '#666',
    fontWeight: 500,
  },
  dots: { display: 'flex', flexWrap: 'wrap', gap: '3px', marginTop: '0.1rem' },
  dot: {
    display: 'inline-block',
    width: '0.5rem',
    height: '0.5rem',
    borderRadius: radius.pill,
    flexShrink: 0,
    boxSizing: 'border-box',
  },
  dotPto: { background: color.brandBlue },
  // A square, not a circle: shape as well as hue separates it from PTO for
  // colour-blind readers (WCAG 1.4.1 — review finding). Shape logic
  // untouched; only the fill color moves to teal.
  dotOther: { background: color.teal, borderRadius: 0 },
  dotPending: { background: 'white', border: `1px dashed ${color.pendingDash}` },
  dayPanel: {
    marginTop: '0.75rem',
    padding: '0.75rem 0.9rem',
    border: '1px solid #c6d9f7',
    borderRadius: '0.5rem',
    background: '#f7f9fd',
    fontSize: '0.88rem',
  },
  dayPanelTitle: { margin: '0 0 0.4rem', fontWeight: 700 },
  closeBtn: {
    marginTop: '0.6rem',
    minHeight: '2.75rem',
    padding: '0.4rem 1.1rem',
    fontSize: '0.85rem',
    fontWeight: 600,
    border: '1px solid #c7d0dd',
    borderRadius: radius.pill,
    background: 'white',
    color: '#1a1a1a',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  dayPanelList: { listStyle: 'none', margin: 0, padding: 0 },
  dayPanelItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.4rem 0',
    borderTop: '1px solid #e3e7ee',
    flexWrap: 'wrap',
  },
  dayPanelName: { fontWeight: 600 },
  dayPanelMeta: { color: '#555' },
} satisfies Record<string, React.CSSProperties>;
